import * as React from 'react'

import { Box, Text, ToggleButton } from '@factorialco/deprecated-design-system'
import {
  TimesheetEditTimesheetRequestFragment,
  UpdateEditTimesheetRequestMutationVariables,
} from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import TimePicker from 'modules/core/components/TimePicker'
import { useFormContext } from 'modules/core/hooks/useForm'
import { convertDate } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { locationTypeOptions } from 'modules/attendance/lib/locationType'
import EditTimesheetRequestUtils from 'modules/attendance/lib/utils/EditTimesheetRequest'

interface Props {
  editTimesheetRequest: TimesheetEditTimesheetRequestFragment
  hasBreaksEnabled?: boolean
  hasLocationEnabled?: boolean
  dayBreaksOptions?: { label: string; value: number }[]
}

const Form = ({
  hasBreaksEnabled,
  dayBreaksOptions,
  editTimesheetRequest,
  hasLocationEnabled,
}: Props) => {
  const form = useFormContext<UpdateEditTimesheetRequestMutationVariables>()
  const hasPaidBreaks = hasFeature('time_tracking_paid_breaks')

  return (
    <>
      <Box>
        <Text.Normal>{i18n.t('clock_in.edit_timesheet_requests.modal.date')}</Text.Normal>
        <Text.Normal weight="bold">
          {convertDate(editTimesheetRequest.date || '', 'backend', 'ui')}
        </Text.Normal>
      </Box>
      {hasBreaksEnabled && (
        <Box paddingTop="s16">
          <Box flexDirection="row" gap="s8">
            <ToggleButton
              toggled={editTimesheetRequest.workable ?? false}
              onClick={() => form.setFieldValue('workable', true)}
              label={i18n.t('time_tracking.table.body.category.work')}
            />
            {
              <ToggleButton
                toggled={!editTimesheetRequest.workable}
                onClick={() => form.setFieldValue('workable', false)}
                label={i18n.t('time_tracking.break')}
              />
            }
          </Box>
        </Box>
      )}
      {!editTimesheetRequest.workable && hasPaidBreaks && !!dayBreaksOptions?.length && (
        <Box paddingTop="s16">
          <SelectField
            label={i18n.t('clock_in.edit_timesheet_requests.modal.break_name')}
            options={dayBreaksOptions || []}
            onChange={(value) =>
              form.setFieldValue('timeSettingsBreakConfigurationId', parseInt(value))
            }
            readonly={EditTimesheetRequestUtils.isDeleteShift(editTimesheetRequest)}
            defaultValue={`${editTimesheetRequest.timeSettingsBreakConfigurationId}`}
          />
        </Box>
      )}
      <Box flexDirection="row" alignItems="center" paddingTop="s16">
        <Box borderRadius={{ all: 'abs008' }}>
          <TimePicker
            value={form.values.clockIn || '00:00'}
            onChange={(value) => form.setFieldValue('clockIn', value)}
          />
        </Box>
        <Box padding="s6">
          <Text.Normal color="grey600">–</Text.Normal>
        </Box>
        <Box borderRadius={{ all: 'abs008' }}>
          <TimePicker
            value={form.values.clockOut || '00:00'}
            onChange={(value) => form.setFieldValue('clockOut', value)}
          />
        </Box>
      </Box>
      {hasLocationEnabled && (
        <Box paddingTop="s16">
          <SelectField
            label={i18n.t('clock_in.locations.modal.title')}
            options={locationTypeOptions()}
            value={form.values.locationType || ''}
            onChange={(value) => form.setFieldValue('workLocation', value)}
          />
        </Box>
      )}
      <Box paddingTop="s16">
        <TextArea
          label={i18n.t('clock_in.edit_timesheet_requests.modal.observations_label')}
          value={form.values.observations || ''}
          onChange={(observations) => form.setFieldValue('observations', observations)}
        />
      </Box>
    </>
  )
}

export default Form
